/**
 * Note Visibility Utilities
 * Based on the integration guide note visibility rules
 */

/**
 * Get note visibility description based on target user role
 * @param {string} targetRole - The role of the user whose note is being viewed
 * @returns {string} Description of who can see the note
 */
export const getNoteVisibilityText = (targetRole) => {
  switch (targetRole) {
    case 'super_admin':
      return 'Visible to super admin only';
    case 'manager':
      return 'Visible to this manager and super admin';
    case 'dealer':
      return 'Visible to this dealer and super admin only';
    case 'subdealer':
      return 'Visible to managers, dealers, and super admin';
    case 'branch':
      return 'Visible to branch managers and super admin';
    default:
      return 'Note visibility varies by role';
  }
};

/**
 * Check if current user can see another user's note
 * Note: Backend handles actual filtering, this is for UI hints only
 * If note field is present in API response, user can see it
 * 
 * @param {object} currentUser - The logged-in user
 * @param {object} targetUser - The user whose note we're checking
 * @returns {boolean} Whether the current user can see the note
 */
export const canSeeNote = (currentUser, targetUser) => {
  if (!currentUser || !targetUser) return false;
  
  const currentRole = currentUser.role;
  const targetRole = targetUser.role;
  
  // Super admin can see all notes
  if (currentRole === 'super_admin') return true;
  
  // Users can see their own notes
  if (currentUser.id === targetUser.id) {
    return true;
  }
  
  // Manager can see subdealer notes in their branch
  if (currentRole === 'manager' && targetRole === 'subdealer') {
    return currentUser.branchId === targetUser.branchId;
  }
  
  // Dealer can see their subdealer notes
  if (currentRole === 'dealer' && targetRole === 'subdealer') {
    return currentUser.dealerId === targetUser.dealerId || 
           currentUser.id === targetUser.dealerId ||
           currentUser.dealerId === targetUser.dealerId;
  }
  
  // Manager can see own note
  if (currentRole === 'manager' && targetRole === 'manager') {
    return currentUser.id === targetUser.id;
  }
  
  // Dealer can see own note
  if (currentRole === 'dealer' && targetRole === 'dealer') {
    return currentUser.id === targetUser.id;
  }
  
  return false;
};

/**
 * Check if user's note field should be displayed in UI
 * Backend filters this automatically - if note field exists, show it
 * 
 * @param {object} user - The user object from API
 * @returns {boolean} Whether to show note field
 */
export const shouldShowNoteField = (user) => {
  return user && user.note !== undefined;
};

/**
 * Get placeholder text for note input based on role
 * @param {string} role - User role
 * @returns {string} Placeholder text
 */
export const getNotePlaceholder = (role) => {
  switch (role) {
    case 'manager':
      return 'Add a note about this manager (visible to super admin)...';
    case 'dealer':
      return 'Add a note about this dealer (visible to super admin)...';
    case 'subdealer':
      return 'Add a note about this subdealer...';
    default:
      return 'Add a note...';
  }
};